import {
  asZoomPayload,
  isMultiplayerMessage,
  normalizeDisplayName
} from '@/zoom/multiplayerProtocol';
import { sendZoomMessage, subscribeToZoomMessages } from '@/integrations/zoom';
import { useZoom } from '@/providers/ZoomProvider';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Account-free Zoom launch screen.
 *
 * A participant opening an invitation lands here. The panel announces itself, listens
 * for the host's active multiplayer demo, and enters it automatically. No legacy API,
 * Cognito account, organization, or database is involved.
 */
export default function ZoomPanel() {
  const zoom = useZoom();
  const navigate = useNavigate();
  const [discovering, setDiscovering] = useState(false);

  const participantId = zoom.userContext?.participantUUID ?? '';
  const displayName = normalizeDisplayName(zoom.userContext?.screenName);
  const isMeetingHost = zoom.userContext?.role === 'host';
  const multiplayerAvailable = useMemo(
    () =>
      zoom.embedded &&
      zoom.inMeeting &&
      !!participantId &&
      zoom.supports('getUserContext') &&
      zoom.supports('sendMessage') &&
      zoom.supports('onMessage'),
    [zoom, participantId]
  );

  useEffect(() => {
    if (!multiplayerAvailable || !participantId || isMeetingHost) return;

    let teardown: (() => void) | undefined;
    let interval: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;
    setDiscovering(true);

    subscribeToZoomMessages((event) => {
      if (!isMultiplayerMessage(event.payload) || event.payload.t !== 'announce') return;
      if (!event.participantUUID || event.participantUUID !== event.payload.f) return;
      if (cancelled || event.payload.f === participantId) return;

      const query = new URLSearchParams({ game: event.payload.g, host: event.payload.h });
      navigate(`/zoom/multiplayer?${query.toString()}`, { replace: true });
    }).then((remove) => {
      if (cancelled) {
        remove();
        return;
      }
      teardown = remove;

      const discover = () =>
        void sendZoomMessage(
          asZoomPayload({
            v: 1,
            t: 'discover',
            f: participantId,
            n: displayName
          })
        );

      discover();
      interval = setInterval(discover, 2000);
    });

    return () => {
      cancelled = true;
      teardown?.();
      if (interval) clearInterval(interval);
    };
  }, [multiplayerAvailable, participantId, displayName, isMeetingHost, navigate]);

  const rows: Array<[string, string]> = [
    ['Running in Zoom', zoom.embedded ? 'yes' : 'no'],
    ['Product', zoom.product],
    ['Context', zoom.runningContext],
    ['In a meeting', zoom.inMeeting ? 'yes' : 'no'],
    ['Zoom role', zoom.userContext?.role ?? '—'],
    ['Display name', zoom.userContext?.screenName ?? '—'],
    ['Client version', zoom.clientVersion ?? '—'],
    ['App authorized', zoom.authStatus ?? '—'],
    ['Panel size', `${window.innerWidth} x ${window.innerHeight}`],
    ['Guest multiplayer', multiplayerAvailable ? 'available' : 'unavailable']
  ];

  return (
    <div className="min-h-screen overflow-y-auto bg-white px-16 py-20">
      <h1 className="text-h3 mb-4">Scribl</h1>

      {!zoom.embedded && (
        <p className="text-body text-body-sm mb-16 text-neutral-400">
          Open this app from the Apps panel in a Zoom meeting to test multiplayer.
        </p>
      )}

      {zoom.embedded && !zoom.inMeeting && (
        <p className="text-body text-body-sm mb-16 text-neutral-400">
          Start or join a meeting, then reopen Scribl from the Apps panel.
        </p>
      )}

      <div className="mb-20 flex flex-col gap-8">
        {isMeetingHost && multiplayerAvailable ? (
          <Link className="btn btn-primary w-full" to="/zoom/multiplayer?host=1">
            Start multiplayer demo
          </Link>
        ) : multiplayerAvailable ? (
          <div className="rounded-xs border border-neutral-200 p-12 text-center">
            <p className="text-body text-body-sm-strong">Waiting for the host</p>
            <p className="text-body text-body-xs mt-4 text-neutral-400">
              {discovering
                ? 'Looking for a Scribl game in this meeting…'
                : 'Ask the meeting host to start a multiplayer demo.'}
            </p>
          </div>
        ) : (
          <p className="text-body text-body-sm rounded-xs border border-neutral-200 p-12">
            Multiplayer messaging is unavailable in this Zoom client. You can still run the solo
            demo.
          </p>
        )}

        <Link className="btn btn-neutral w-full" to="/zoom/demo">
          Try solo demo
        </Link>
        <p className="text-body text-body-xs text-neutral-400">
          Neither mode requires a Scribl account. Multiplayer state exists only for this meeting.
        </p>
      </div>

      <details className="rounded-xs border border-neutral-200 p-12">
        <summary className="text-body text-body-sm-strong cursor-pointer">
          Connection details
        </summary>
        <table className="mt-12 w-full">
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td className="text-body text-body-xs py-2 pr-12 text-neutral-400 align-top">
                  {label}
                </td>
                <td className="text-body text-body-xs py-2 break-words">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {zoom.embedded && zoom.unsupportedApis.length > 0 && (
          <div className="mt-12">
            <p className="text-body text-body-xs text-neutral-400">
              Unsupported in this client ({zoom.unsupportedApis.length})
            </p>
            <p className="text-body text-body-xs break-words">{zoom.unsupportedApis.join(', ')}</p>
          </div>
        )}
      </details>
    </div>
  );
}
